import { getErrMsg } from '../lib/errorMsg';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { Eye, EyeOff, TrendingUp, ShieldAlert, FileSearch, LayoutDashboard, Hexagon, Activity } from 'lucide-react';

const SPHERE_IMAGE = 'https://static.prod-images.emergentagent.com/jobs/f8c3a967-b3f4-46f0-aa51-86589a8e5878/images/3a3ae21740847445230d89af164072876f99aaed19de4b6f6fb5c4392b77563a.png';

const FEATURES = [
  { icon: TrendingUp, title: 'Dynamic RM Indexing', desc: 'Track aluminium, SS, CRCA, brass price movement in real time.', color: 'text-cyan-400' },
  { icon: ShieldAlert, title: 'Margin Protection Engine', desc: 'Instant alerts when part margins fall outside threshold.', color: 'text-amber-400' },
  { icon: FileSearch, title: 'Excel Smart Extraction', desc: 'Upload legacy costing sheets and auto-structure your data.', color: 'text-emerald-400' },
  { icon: Activity, title: 'RM Variance Tracking', desc: 'Compare locked vs market prices across all parts instantly.', color: 'text-orange-400' },
  { icon: LayoutDashboard, title: 'Cost Command Dashboard', desc: 'Visualize true manufacturing margins across customers and parts.', color: 'text-sky-400' },
  { icon: Hexagon, title: 'Multi-Parser Intelligence', desc: 'Auto-detect customer file formats with dedicated parsers.', color: 'text-violet-400' },
];

const ORBIT_KEYWORDS = [
  { label: 'CRCA', angle: 0, radius: 52, color: '#22d3ee' },
  { label: 'Aluminium', angle: 40, radius: 50, color: '#f97316' },
  { label: 'Stainless Steel', angle: 80, radius: 53, color: '#a78bfa' },
  { label: 'Brass', angle: 120, radius: 49, color: '#fbbf24' },
  { label: 'Margin %', angle: 160, radius: 51, color: '#34d399' },
  { label: 'Scrap Recovery', angle: 200, radius: 52, color: '#38bdf8' },
  { label: 'Net RMC', angle: 240, radius: 50, color: '#fb923c' },
  { label: 'BOP Cost', angle: 280, radius: 53, color: '#c084fc' },
  { label: 'Plating', angle: 320, radius: 49, color: '#2dd4bf' },
];

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

export default function Login() {
  const [tab, setTab] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = tab === 'login' ? '/auth/login' : '/auth/register';
      const payload = tab === 'login' ? { email, password } : { email, password, name };
      const res = await api.post(endpoint, payload);
      login(res.data.user, res.data.token);
      navigate('/dashboard');
      toast.success(tab === 'login' ? 'Welcome back!' : 'Account created successfully!');
    } catch (err) {
      toast.error(getErrMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: '#090c14' }}>
      {/* CSS Animations */}
      <style>{`
        @keyframes sphereRotate {
          0%   { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes spherePulse {
          0%, 100% { filter: drop-shadow(0 0 30px rgba(14,165,233,0.25)); transform: scale(1); }
          50%      { filter: drop-shadow(0 0 60px rgba(14,165,233,0.45)) drop-shadow(0 0 100px rgba(249,115,22,0.15)); transform: scale(1.02); }
        }
        @keyframes orbitSpin {
          0%   { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes tagFloat {
          0%, 100% { transform: translateY(0px); opacity: 0.85; }
          50%      { transform: translateY(-6px); opacity: 1; }
        }
        .sphere-img {
          animation: spherePulse 5s ease-in-out infinite;
        }
        .orbit-ring {
          animation: orbitSpin 40s linear infinite;
        }
        .orbit-ring-reverse {
          animation: orbitSpin 55s linear infinite reverse;
        }
        .keyword-tag {
          animation: tagFloat 4s ease-in-out infinite;
        }
      `}</style>

      {/* Ambient background glows */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[10%] left-[20%] w-[700px] h-[700px] rounded-full opacity-[0.12]"
          style={{ background: 'radial-gradient(circle, #0ea5e9 0%, transparent 70%)' }} />
        <div className="absolute bottom-[20%] left-[35%] w-[500px] h-[500px] rounded-full opacity-[0.08]"
          style={{ background: 'radial-gradient(circle, #f97316 0%, transparent 70%)' }} />
      </div>

      <div className="relative z-10 min-h-screen flex flex-col">

        {/* Top bar — Logo + Company */}
        <div className="flex items-center justify-between px-8 lg:px-12 pt-6 pb-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #0ea5e9 0%, #f97316 100%)' }}>
              <Hexagon size={22} className="text-white" strokeWidth={2.5} />
            </div>
            <span className="font-heading font-bold text-2xl text-white tracking-tight">CostSphere</span>
          </div>
          {/* Company logo */}
          <div className="flex items-center gap-3">
            <img src="/anmol-logo-new.jpg" alt="Anmol Metcomp"
              className="h-12 w-auto object-contain rounded"
              style={{ filter: 'brightness(1.1)' }} />
            <div className="hidden sm:block">
              <p className="text-white font-heading font-bold text-sm leading-tight">Advanced Anmol</p>
              <p className="text-slate-400 text-[11px]">Metcomp Pvt Ltd</p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col lg:flex-row items-center">

          {/* Left: Sphere + Text — takes most space */}
          <div className="flex-1 flex flex-col items-center justify-center px-6 lg:px-12 py-6 lg:py-0">
            {/* Headline */}
            <h1 className="font-heading font-black text-white text-center leading-tight mb-2"
              style={{ fontSize: 'clamp(1.6rem, 3vw, 2.5rem)' }}>
              360° Manufacturing Cost Intelligence
            </h1>
            <p className="text-sm font-bold uppercase tracking-[0.25em] mb-8 text-center"
              style={{ color: '#f97316' }}>
              All Costs Orbit Inside One Sphere
            </p>

            {/* Sphere with orbiting keywords */}
            <div className="relative" style={{ width: 'min(520px, 75vw)', height: 'min(520px, 75vw)' }}>
              {/* Outer orbit ring */}
              <div className="absolute inset-[-15%] orbit-ring pointer-events-none">
                <svg viewBox="0 0 100 100" className="w-full h-full">
                  <ellipse cx="50" cy="50" rx="48" ry="48" fill="none" stroke="rgba(14,165,233,0.12)" strokeWidth="0.3" />
                </svg>
              </div>
              {/* Inner orbit ring (reverse) */}
              <div className="absolute inset-[-5%] orbit-ring-reverse pointer-events-none">
                <svg viewBox="0 0 100 100" className="w-full h-full">
                  <ellipse cx="50" cy="50" rx="46" ry="46" fill="none" stroke="rgba(249,115,22,0.08)" strokeWidth="0.25" strokeDasharray="2 3" />
                </svg>
              </div>

              {/* Sphere image — edges fade into background */}
              <img src={SPHERE_IMAGE} alt="CostSphere visualization"
                className="sphere-img w-full h-full object-contain"
                style={{
                  maskImage: 'radial-gradient(ellipse 65% 65% at 50% 50%, black 40%, transparent 72%)',
                  WebkitMaskImage: 'radial-gradient(ellipse 65% 65% at 50% 50%, black 40%, transparent 72%)',
                }} />

              {/* Floating keyword tags */}
              {ORBIT_KEYWORDS.map(({ label, angle, radius, color }, i) => {
                const rad = (angle * Math.PI) / 180;
                const x = 50 + radius * Math.cos(rad);
                const y = 50 + radius * Math.sin(rad);
                return (
                  <div key={label}
                    className="keyword-tag absolute pointer-events-none"
                    style={{
                      left: `${x}%`, top: `${y}%`,
                      transform: 'translate(-50%, -50%)',
                      animationDelay: `${i * 0.45}s`,
                    }}>
                    <span className="inline-block px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider whitespace-nowrap"
                      style={{
                        color,
                        background: `${color}12`,
                        border: `1px solid ${color}30`,
                        textShadow: `0 0 12px ${color}60`,
                      }}>
                      {label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right: Login Card */}
          <div className="w-full lg:w-[400px] xl:w-[420px] flex items-center justify-center px-6 py-8 lg:py-0 lg:pr-12">
            <div className="w-full max-w-sm">
              <div className="rounded-xl p-7" data-testid="login-card"
                style={{
                  background: 'rgba(12, 16, 26, 0.7)',
                  backdropFilter: 'blur(24px)',
                  WebkitBackdropFilter: 'blur(24px)',
                  border: '1px solid rgba(255,255,255,0.07)',
                  boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
                }}>

                <h2 className="font-heading font-bold text-lg text-white mb-5">
                  {tab === 'login' ? 'Welcome to CostSphere' : 'Create Account'}
                </h2>

                {/* Tabs */}
                <div className="flex rounded-lg p-1 mb-5" style={{ background: 'rgba(255,255,255,0.05)' }}>
                  {['login', 'register'].map((t) => (
                    <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t}`}
                      className={`flex-1 py-1.5 text-[10px] font-semibold uppercase tracking-wider rounded-md transition-all duration-200
                        ${tab === t ? 'text-white shadow-sm' : 'text-slate-400 hover:text-slate-200'}`}
                      style={tab === t ? { background: 'rgba(255,255,255,0.1)' } : {}}>
                      {t === 'login' ? 'Sign In' : 'Register'}
                    </button>
                  ))}
                </div>

                <form onSubmit={handleSubmit} className="space-y-3.5" autoComplete="on">
                  {tab === 'register' && (
                    <div>
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1 block">Full Name</label>
                      <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                        placeholder="Your full name" required data-testid="input-name"
                        name="name" autoComplete="name"
                        className="w-full h-10 px-3.5 rounded-lg text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-cyan-500/50 transition-all"
                        style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.07)' }} />
                    </div>
                  )}
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1 block">Email</label>
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@company.com" required data-testid="input-email"
                      name="email" id="email" autoComplete="username"
                      className="w-full h-10 px-3.5 rounded-lg text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-cyan-500/50 transition-all"
                      style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.07)' }} />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1 block">Password</label>
                    <div className="relative">
                      <input type={showPass ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter password" required data-testid="input-password"
                        name="password" id="password"
                        autoComplete={tab === 'login' ? 'current-password' : 'new-password'}
                        className="w-full h-10 px-3.5 pr-10 rounded-lg text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-cyan-500/50 transition-all"
                        style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.07)' }} />
                      <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowPass(!showPass); }}
                        data-testid="toggle-password-btn"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors z-10 p-1">
                        {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  <button type="submit" disabled={loading} data-testid="submit-btn"
                    className="w-full h-10 text-sm font-bold uppercase tracking-widest rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:brightness-125"
                    style={{
                      background: 'linear-gradient(135deg, rgba(14,165,233,0.25) 0%, rgba(249,115,22,0.2) 100%)',
                      border: '1px solid rgba(255,255,255,0.12)',
                      color: '#fff',
                    }}>
                    {loading ? 'Please wait...' : (tab === 'login' ? 'Access Platform' : 'Create Account')}
                  </button>
                </form>


                <div className="mt-5 text-center">
                  <p className="text-[10px] text-slate-500 italic">Enterprise Cost Intelligence Platform</p>
                  <p className="text-[10px] text-slate-400 font-semibold mt-0.5">Advanced Anmol Metcomp Pvt Ltd</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Feature Cards */}
        <div className="px-6 lg:px-12 pb-6 pt-2">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2.5">
            {FEATURES.map(({ icon: Icon, title, desc, color }) => (
              <div key={title} className="rounded-lg p-3.5 transition-all duration-300 hover:scale-[1.03]"
                style={{
                  background: 'rgba(255,255,255,0.025)',
                  border: '1px solid rgba(255,255,255,0.05)',
                }}>
                <Icon size={16} className={`${color} mb-2`} />
                <p className="text-white text-[11px] font-bold mb-0.5 leading-tight">{title}</p>
                <p className="text-slate-500 text-[9px] leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
